import { useContext } from 'react'

import { isNil } from 'lodash'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { TrackingProperties, track as originalTrack } from 'modules/core/lib/Tracking'
import companies from 'modules/core/store/collections/companies'

import { reportStoreContext } from 'modules/reports/contexts/reportStoreContext'
import { ReportEditorStore } from 'modules/reports/lib/Store'
import { events } from 'modules/reports/lib/events'

export enum FieldSelectionType {
  Data,
  X,
  Y,
  GroupBy,
  BreakdownBy,
}

/**
 * Creates a track function that automatically adds properties from the report store.
 *
 * It automatically gets the store from the context, but one can be manually provided instead.
 *
 * @param providedStore The store to use, if not provided will use the one from the context
 * @returns A new track function to use
 */
export const useReportTracking = (providedStore?: Partial<ReportEditorStore>) => {
  const contextStore = useContext(reportStoreContext)
  const store = providedStore ?? contextStore
  const { access } = useCurrentSession()

  const track: (...params: Parameters<typeof originalTrack>) => void = useUnsafeCallback(
    (eventInput, eventProperties) => {
      const filledProperties: TrackingProperties = {
        company_id: companies.current.id,
        user_id: access.id,
      }

      if (store) {
        if (!isNil(store.saved)) {
          filledProperties.report_saved = !!store.saved
        }
        filledProperties.is_sql = !!store.sqlQuery
        filledProperties.chart_type = store.reportStruct?.type
      }

      originalTrack(eventInput, { ...filledProperties, ...eventProperties })
    },
    [store, access]
  )

  /** Specialized tracker for builder field selection */
  const trackFieldChange = useUnsafeCallback(
    (type: FieldSelectionType) => {
      switch (type) {
        case FieldSelectionType.Data:
          track(events.dataSelected)
          break
        case FieldSelectionType.X:
          track(events.xAxisSelected)
          break
        case FieldSelectionType.Y:
          track(events.yAxisSelected)
          break
        case FieldSelectionType.GroupBy:
          track(events.groupBySelected)
          break
        case FieldSelectionType.BreakdownBy:
          track(events.breakdownBySelected)
          break
      }
    },
    [track]
  )

  return {
    track,
    trackFieldChange,
  }
}
